export interface FolderTreeEntry {
  tempId: string
  name: string
  parentTempId: string | null
  relativePath: string
}

export interface FileEntry {
  file: File
  folderTempId: string | null
  relativePath: string
}

export interface FolderUploadData {
  folders: FolderTreeEntry[]
  files: FileEntry[]
  rootFolderName: string
  totalSize: number
}

export interface TreeNode {
  name: string
  tempId: string
  children: TreeNode[]
  fileCount: number
}

let tempIdCounter = 0
const generateTempId = (): string => `tmp-${Date.now()}-${++tempIdCounter}`

/**
 * Reads dropped items from DataTransfer, detecting directories via webkitGetAsEntry().
 * Recursively traverses FileSystemDirectoryEntry using createReader().readEntries().
 */
export async function readDroppedItems(dataTransfer: DataTransfer): Promise<FolderUploadData> {
  const folders: FolderTreeEntry[] = []
  const files: FileEntry[] = []
  let totalSize = 0
  let rootFolderName = ''

  const items = Array.from(dataTransfer.items)
  const entries = items
    .map((item) => item.webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntry => entry != null)

  const pathToTempId = new Map<string, string>()

  const readAllEntries = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      const allEntries: FileSystemEntry[] = []
      const readBatch = () => {
        reader.readEntries((batch) => {
          if (batch.length === 0) {
            resolve(allEntries)
          } else {
            allEntries.push(...batch)
            readBatch()
          }
        }, reject)
      }
      readBatch()
    })
  }

  const getFile = (fileEntry: FileSystemFileEntry): Promise<File> => {
    return new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject)
    })
  }

  const traverseEntry = async (
    entry: FileSystemEntry,
    parentTempId: string | null,
    parentPath: string,
  ) => {
    if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry
      const tempId = generateTempId()
      const relativePath = parentPath ? `${parentPath}/${dirEntry.name}` : dirEntry.name

      pathToTempId.set(relativePath, tempId)

      folders.push({
        tempId,
        name: dirEntry.name,
        parentTempId,
        relativePath,
      })

      const reader = dirEntry.createReader()
      const childEntries = await readAllEntries(reader)

      for (const child of childEntries) {
        await traverseEntry(child, tempId, relativePath)
      }
    } else if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry
      const file = await getFile(fileEntry)
      const relativePath = parentPath ? `${parentPath}/${file.name}` : file.name

      totalSize += file.size
      files.push({
        file,
        folderTempId: parentTempId,
        relativePath,
      })
    }
  }

  for (const entry of entries) {
    if (entry.isDirectory && !rootFolderName) {
      rootFolderName = entry.name
    }
    await traverseEntry(entry, null, '')
  }

  if (!rootFolderName && folders.length > 0) {
    rootFolderName = folders[0].name
  }

  return { folders, files, rootFolderName, totalSize }
}

/**
 * Reads files from a folder input (webkitdirectory).
 * Parses file.webkitRelativePath to extract folder structure.
 */
export function readInputFolder(fileList: FileList): FolderUploadData {
  const folders: FolderTreeEntry[] = []
  const files: FileEntry[] = []
  let totalSize = 0

  const pathToTempId = new Map<string, string>()
  let rootFolderName = ''

  const fileArray = Array.from(fileList)

  for (const file of fileArray) {
    const relativePath = (file as any).webkitRelativePath || file.name
    const parts = relativePath.split('/')

    if (!rootFolderName && parts.length > 1) {
      rootFolderName = parts[0]
    }

    // Create folder entries for each path segment (excluding the filename)
    for (let i = 0; i < parts.length - 1; i++) {
      const folderPath = parts.slice(0, i + 1).join('/')
      if (!pathToTempId.has(folderPath)) {
        const tempId = generateTempId()
        pathToTempId.set(folderPath, tempId)

        const parentPath = i > 0 ? parts.slice(0, i).join('/') : null
        const parentTempId = parentPath ? pathToTempId.get(parentPath) || null : null

        folders.push({
          tempId,
          name: parts[i],
          parentTempId,
          relativePath: folderPath,
        })
      }
    }

    // Determine which folder the file belongs to
    const fileFolderPath = parts.length > 1 ? parts.slice(0, parts.length - 1).join('/') : null
    const folderTempId = fileFolderPath ? pathToTempId.get(fileFolderPath) || null : null

    totalSize += file.size
    files.push({
      file,
      folderTempId,
      relativePath,
    })
  }

  return { folders, files, rootFolderName, totalSize }
}

/**
 * Builds a nested tree structure for preview display.
 */
export function formatFolderTree(data: FolderUploadData): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>()

  // Create all nodes
  for (const folder of data.folders) {
    nodeMap.set(folder.tempId, {
      name: folder.name,
      tempId: folder.tempId,
      children: [],
      fileCount: 0,
    })
  }

  // Count files per folder
  for (const file of data.files) {
    if (file.folderTempId) {
      const node = nodeMap.get(file.folderTempId)
      if (node) node.fileCount++
    }
  }

  // Build tree
  const roots: TreeNode[] = []
  for (const folder of data.folders) {
    const node = nodeMap.get(folder.tempId)!
    if (folder.parentTempId) {
      const parent = nodeMap.get(folder.parentTempId)
      if (parent) {
        parent.children.push(node)
      } else {
        roots.push(node)
      }
    } else {
      roots.push(node)
    }
  }

  return roots
}

/**
 * Check if the browser supports folder uploads.
 */
export function isFolderUploadSupported(): boolean {
  const input = document.createElement('input')
  return 'webkitdirectory' in input
}

/**
 * Check if a DataTransfer contains any directories.
 */
export function hasDirectoryEntries(dataTransfer: DataTransfer): boolean {
  const items = Array.from(dataTransfer.items)
  return items.some((item) => {
    const entry = item.webkitGetAsEntry?.()
    return entry?.isDirectory
  })
}
